<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" />
          </button>
          <h5 class="page-title mt-2">
            Billing Detail
            <span v-if="billing.reference" class="batch-pill ms-2">{{ billing.reference }}</span>
          </h5>
          <p class="page-sub" v-if="billing.group?.name">{{ billing.group.name }}</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search invoices..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="JSON.parse(status.pending)">Pending</option>
            <option :value="JSON.parse(status.partial)">Partial Payment</option>
            <option :value="JSON.parse(status.completed)">Completed</option>
            <option :value="JSON.parse(status.overdue)">Overdue</option>
            <option :value="JSON.parse(status.abandoned)">Abandoned</option>
            <option :value="JSON.parse(status.all)">All Statuses</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', 'billing_invoices_'+billing.reference, 'Billing Invoices - '+billing.reference)">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('billing_invoices_'+billing.reference+'.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <!-- Bulk Reminder -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle"
              :class="selectedModel.length === 0 || permission.send_reminder != 1 ? 'btn-outline--disabled' : ''"
              data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="tasks" /> Bulk Action
              <span v-if="selectedModel.length > 0" class="bulk-count">{{ selectedModel.length }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item"
                  :disabled="permission.send_reminder != 1 || selectedModel.length === 0"
                  data-bs-toggle="modal" data-bs-target="#bulk_reminder_modal">
                  <font-awesome-icon icon="bell" class="me-2" /> Send Reminder
                </button>
              </li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading invoices...</p>
        </div>
  
        <div v-else-if="invoices.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="file-invoice" /></div>
          <p class="state-title">No invoices found</p>
          <p class="state-sub">There are no invoices in this billing yet.</p>
        </div>
  
        <template v-else>
  
          <!-- Billing Meta -->
          <div class="billing-meta-strip">
            <div class="meta-item">
              <span class="meta-key">Billing Date</span>
              <span class="meta-val">{{ formatDate(billing.billing_date) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-key">Due Date</span>
              <span class="meta-val">{{ formatDate(billing.due_date) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-key">Billing Group</span>
              <span class="meta-val">{{ billing?.group?.name || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-key">Currency</span>
              <span class="meta-val">{{ billing?.currency?.name }} ({{ billing?.currency?.symbol }})</span>
            </div>
            <div class="meta-item">
              <span class="meta-key">Total Invoices</span>
              <span class="meta-val fw-bold">{{ pagination.total }}</span>
            </div>
          </div>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
              <span v-if="selectedModel.length > 0" class="selected-hint">· {{ selectedModel.length }} selected</span>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option><option value="10">10</option><option value="20">20</option>
                <option value="30">30</option><option value="50">50</option><option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" class="row-check" v-model="allSelected" @change="selectAll" />
                  </th>
                  <th>#</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Paid</th>
                  <th class="text-right">Balance</th>
                  <th>Status</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in invoices" :key="index"
                  :class="selectedModel.includes(row.id) ? 'row--selected' : ''">
  
                  <td class="col-check">
                    <input type="checkbox" class="row-check" v-model="selectedModel" :value="row.id" />
                  </td>
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td><span class="batch-code">{{ row.invoice_no }}</span></td>
  
                  <td>
                    <div class="customer-cell">
                      <div class="customer-avatar">{{ customerInitials(row.customer) }}</div>
                      <span class="customer-name">{{ row.customer?.customer_name || '—' }}</span>
                    </div>
                  </td>
  
                  <td class="text-muted-cell" style="white-space:nowrap">{{ formatDate(row.invoice_date) }}</td>
  
                  <td class="text-right fw-bold">{{ row.currency?.symbol }}{{ formatPrice(row.total_amount) }}</td>
                  <td class="text-right earning-value fw-bold">{{ row.currency?.symbol }}{{ formatPrice(row.paid_amount) }}</td>
                  <td class="text-right" :class="row.balance_amount > 0 ? 'deduction-value fw-bold' : 'text-muted-cell'">
                    {{ row.currency?.symbol }}{{ formatPrice(row.balance_amount) }}
                  </td>
  
                  <td>
                    <span class="status-badge" :class="statusClass(row.status)">{{ getStatus(row) }}</span>
                  </td>
  
                  <td class="col-actions">
                    <div class="dropdown">
                      <button class="action-dropdown-btn" type="button"
                        data-bs-toggle="dropdown" data-bs-strategy="fixed" aria-expanded="false">
                        <font-awesome-icon icon="ellipsis-v" />
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end action-menu">
  
                        <li>
                          <router-link class="action-menu-item" :to="$url('/invoice/detail/' + row.id)"
                            :class="permission.view_invoice != 1 ? 'action-menu-item--disabled' : ''">
                            <font-awesome-icon icon="eye" class="action-menu-icon action-menu-icon--view" />
                            View Details
                          </router-link>
                        </li>
  
                        <li v-if="row.paid_amount > 0">
                          <button class="action-menu-item" @click="viewPayments(index)">
                            <font-awesome-icon icon="money-bill-wave" class="action-menu-icon action-menu-icon--green" />
                            View Payments
                          </button>
                        </li>
  
                        <li v-if="row.status == 0">
                          <router-link class="action-menu-item" :to="$url('/invoice/update/' + row.id)"
                            :class="permission.update_invoice != 1 ? 'action-menu-item--disabled' : ''">
                            <font-awesome-icon icon="pen" class="action-menu-icon action-menu-icon--edit" />
                            Edit Invoice
                          </router-link>
                        </li>
  
                        <li>
                          <router-link class="action-menu-item" :to="$url('/invoice/' + row.id + '/re-create')"
                            :class="permission.create_invoice != 1 ? 'action-menu-item--disabled' : ''">
                            <font-awesome-icon icon="copy" class="action-menu-icon action-menu-icon--blue" />
                            Re-create Invoice
                          </router-link>
                        </li>
  
                        <li v-if="row.status != 2">
                          <router-link class="action-menu-item" :to="$url('/invoice/record-payment/' + row.id)"
                            :class="permission.record_invoice_payment != 1 ? 'action-menu-item--disabled' : ''">
                            <font-awesome-icon icon="plus-circle" class="action-menu-icon action-menu-icon--green" />
                            Record Payment
                          </router-link>
                        </li>
  
                        <li v-if="row.status != 2">
                          <button class="action-menu-item" @click="openReminderModal(index)"
                            :disabled="permission.send_reminder != 1">
                            <font-awesome-icon icon="bell" class="action-menu-icon action-menu-icon--orange" />
                            Send Reminder
                          </button>
                        </li>
  
                        <li v-if="row.is_reocurring == 1">
                          <button class="action-menu-item" @click="cancelRecurring(row.id)"
                            :disabled="permission.update_invoice != 1">
                            <font-awesome-icon icon="ban" class="action-menu-icon" style="color:#7c3aed;" />
                            Cancel Recurring
                          </button>
                        </li>
  
                        <li v-if="row.status == 0 && permission.delete_invoice == 1">
                          <hr class="dropdown-divider" />
                          <button class="action-menu-item action-menu-item--danger" @click="deleteInvoice(row.id)">
                            <font-awesome-icon icon="trash" class="action-menu-icon" />
                            Delete
                          </button>
                        </li>
  
                      </ul>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
  
      <!-- ── Payments Modal ── -->
      <div id="payments-modal" class="modal fade" tabindex="-1" aria-hidden="true">
        <div v-if="currentData" class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(22,163,74,.1);color:#16a34a;">
                <font-awesome-icon icon="money-bill-wave" />
              </div>
              <div>
                <h6 class="modal-clean-title">Payment History</h6>
                <p class="modal-clean-sub">Invoice #{{ currentData.invoice_no }} · {{ currentData.customer?.customer_name }}</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closePaymentModal"></button>
            </div>
            <div class="modal-body p-0">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Payee</th>
                    <th class="text-right">Expected ({{ currentData?.currency?.symbol }})</th>
                    <th class="text-right">Paid ({{ currentData?.currency?.symbol }})</th>
                    <th class="text-right">Balance ({{ currentData?.currency?.symbol }})</th>
                    <th>Channel</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in currentData.payments" :key="i">
                    <td class="text-muted-cell">{{ i + 1 }}</td>
                    <td class="fw-bold">{{ p?.payee_name ?? `${p?.user?.first_name} ${p?.user?.last_name}` }}</td>
                    <td class="text-right">{{ $filters.formatMoney(p?.expected_amount) }}</td>
                    <td class="text-right earning-value fw-bold">{{ $filters.formatMoney(p?.paid_amount) }}</td>
                    <td class="text-right" :class="p?.balance_amount > 0 ? 'deduction-value' : 'text-muted-cell'">{{ $filters.formatMoney(p?.balance_amount) }}</td>
                    <td class="text-muted-cell">{{ p?.gateway?.name ?? 'Recorded Manually' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="modal-footer border-0">
              <button type="button" class="btn-outline" @click="closePaymentModal">Close</button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ── Send Reminder Modal ── -->
      <div class="modal fade" id="reminder_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon" :class="currentData?.customer?.email ? 'modal-icon--blue' : 'modal-icon--warning'">
                <font-awesome-icon :icon="currentData?.customer?.email ? 'bell' : 'exclamation-triangle'" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">{{ currentData?.customer?.email ? 'Send Reminder' : 'No Email Found' }}</p>
              <p class="modal-subtext" v-if="currentData?.customer?.email">
                Send a payment reminder to <strong>{{ currentData?.customer?.customer_name }}</strong> for invoice
                <strong>#{{ currentData?.invoice_no }}</strong>?
              </p>
              <p class="modal-subtext" v-else>
                No email address found for <strong>{{ currentData?.customer?.customer_name }}</strong>.
                Please add an email in Settings before sending a reminder.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" @click="closeReminderModal">
                {{ currentData?.customer?.email ? 'Cancel' : 'Close' }}
              </button>
              <button v-if="currentData?.customer?.email" type="button" class="modal-btn modal-btn--blue"
                :disabled="crudLoading" @click="sendReminder(currentData.id)">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <font-awesome-icon v-else icon="bell" class="me-1" />
                {{ crudLoading ? 'Sending...' : 'Send Reminder' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ── Bulk Reminder Modal ── -->
      <div class="modal fade" id="bulk_reminder_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--blue"><font-awesome-icon icon="bell" /></div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Send Bulk Reminder</p>
              <p class="modal-subtext">
                Send a payment reminder to all <strong>{{ selectedModel.length }} selected customers</strong> on pending payments?
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" @click="closeBulkReminderModal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--blue" :disabled="crudLoading" @click="sendBulkReminder">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <font-awesome-icon v-else icon="bell" class="me-1" />
                {{ crudLoading ? 'Sending...' : 'Yes, Send All' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import moment from 'moment';
  import Header from './component/header.vue';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Invoice Number','Customer','Invoice Date','Total Amount','Total Paid','Total Balance','Currency','Status'],
        invoices:      [],
        billing:       {},
        loading:       false,
        currentData:   {},
        allSelected:   false,
        selectedModel: [],
        crudLoading:   false,
  
        status: {
          all:       "['0','1','2','3','4']".replace(/'/g, '"'),
          pending:   "['0']".replace(/'/g, '"'),
          partial:   "['1']".replace(/'/g, '"'),
          completed: "['2']".replace(/'/g, '"'),
          overdue:   "['3']".replace(/'/g, '"'),
          abandoned: "['4']".replace(/'/g, '"'),
        },
  
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 10, search: '', column: 0, dir: 'desc', status: [0,1,2,3,4],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      getStatus(data) {
        return ['Pending','Partial Payment','Completed','Overdue','Abandoned'][data.status] ?? '—';
      },
      statusClass(status) {
        return ['status--pending','status--partial','status--completed','status--overdue','status--abandoned'][status] ?? '';
      },
      customerInitials(customer) {
        if (!customer?.customer_name) return '?';
        return customer.customer_name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
      },
  
      dataToPrint(details) {
        this.print_content = details.map((inv, idx) => ({
          sn:             idx + 1,
          invoice_no:     inv.invoice_no,
          customer_name:  inv?.customer?.customer_name,
          invoice_date:   inv.invoice_date,
          total_amount:   this.formatPrice(inv.total_amount),
          paid_amount:    this.formatPrice(inv.paid_amount),
          balance_amount: this.formatPrice(inv.balance_amount),
          currency:       `${inv?.currency?.name} (${inv?.currency?.code})`,
          status:         this.getStatus(inv),
        }));
      },
  
      async fetchData(url = `/billing/invoices/${this.$route.params.id}`) {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.billing  = response.data.data.billing;
            this.invoices = response.data.data.invoices.data;
            this.dataToPrint(this.invoices);
            this.configPagination(response.data.data.invoices);
            this.loading = false; this.crudLoading = false;
          })
          .catch(() => { this.loading = false; this.crudLoading = false; });
      },
  
      viewPayments(index)       { this.currentData = this.invoices[index]; window.$('#payments-modal').modal('show'); },
      closePaymentModal()       { window.$('#payments-modal').modal('hide'); },
      openReminderModal(index)  { this.currentData = this.invoices[index]; window.$('#reminder_modal').modal('show'); },
      closeReminderModal()      { window.$('#reminder_modal').modal('hide'); },
      closeBulkReminderModal()  { window.$('#bulk_reminder_modal').modal('hide'); },
  
      selectAll() { this.selectedModel = this.allSelected ? this.invoices.map(i => i.id) : []; },
  
      async cancelRecurring(id) {
        axios.put(`/invoice/cancel-recurring/${id}`)
          .then(r => { if (r.data.status == 200) { this.fetchData(); this.toast.success(r.data.message); } })
          .catch(() => {});
      },
  
      async deleteInvoice(id) {
        axios.delete(`/invoice/delete/${id}`)
          .then(r => { if (r.data.status == 200) { this.fetchData(); this.toast.success(r.data.message); } })
          .catch(() => {});
      },
  
      async sendReminder(id) {
        this.crudLoading = true;
        axios.get(`/invoice/send-reminder/${id}`)
          .then(r => {
            this.crudLoading = false;
            if (r.data.status == 200) { this.fetchData(); this.toast.success(r.data.message); this.closeReminderModal(); }
            else { this.toast.error(r.data.message); }
          })
          .catch(() => { this.crudLoading = false; });
      },
  
      sendBulkReminder() {
        if (!this.selectedModel.length) { this.toast.error('Nothing was selected!'); return; }
        this.crudLoading = true;
        axios.post('/invoice/send-bulk-reminder', { invoices: this.selectedModel })
          .then(r => {
            this.toast.success(r.data.message); this.selectedModel = [];
            this.crudLoading = false; this.closeBulkReminderModal();
          })
          .catch(error => {
            this.crudLoading = false;
            this.toast.error(error.response?.data?.message ?? 'Bulk reminder failed');
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      formatDate(date) { return moment(date).format('MMM DD, YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  .btn-back     { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; }
  .btn-back:hover { color:#1a2b45; }
  .batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  .ms-2 { margin-left:8px; } .me-1 { margin-right:4px; } .me-2 { margin-right:8px; } .mt-2 { margin-top:8px; } .mt-3 { margin-top:12px; } .ms-auto { margin-left:auto; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .btn-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; position:relative; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline--disabled { opacity:.5; pointer-events:none; }
  .btn-outline::after { display:none !important; }
  .bulk-count  { display:inline-flex; align-items:center; justify-content:center; background:#004AAD; color:#fff; border-radius:10px; font-size:11px; font-weight:700; padding:1px 6px; margin-left:4px; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Billing Meta Strip ── */
  .billing-meta-strip { display:flex; align-items:stretch; border-bottom:1px solid #e4eaf2; background:#f8fafc; flex-wrap:wrap; }
  .meta-item    { flex:1; min-width:140px; padding:14px 20px; border-right:1px solid #e4eaf2; }
  .meta-item:last-child { border-right:none; }
  .meta-key     { display:block; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin-bottom:5px; }
  .meta-val     { font-size:13.5px; font-weight:600; color:#1a2b45; }
  .fw-bold      { font-weight:700; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .selected-hint { color:#004AAD; font-weight:600; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row--selected { background:#edf4ff; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-check   { width:40px; padding-left:16px !important; }
  .col-actions { width:48px; text-align:center; }
  .text-right  { text-align:right; }
  .earning-value   { color:#16a34a; }
  .deduction-value { color:#e03535; }
  
  .row-check   { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }
  .batch-code  { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  .customer-cell   { display:flex; align-items:center; gap:9px; }
  .customer-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
  .customer-name   { font-size:13px; font-weight:600; color:#1a2b45; }
  
  .status-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
  .status--pending   { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--partial   { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .status--completed { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--overdue   { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .status--abandoned { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  .action-dropdown-btn { width:30px; height:30px; border-radius:8px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:13px; color:#7a90b0; cursor:pointer; transition:background .15s; margin:auto; }
  .action-dropdown-btn:hover { background:#f4f7fb; color:#1a2b45; }
  .action-menu { min-width:185px; padding:6px; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); }
  .action-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:8px 12px; font-size:13px; color:#1a2b45; background:transparent; border:none; border-radius:7px; cursor:pointer; text-decoration:none; transition:background .12s; }
  .action-menu-item:hover:not(:disabled):not(.action-menu-item--disabled) { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  .action-menu-item--disabled { opacity:.4; cursor:not-allowed; pointer-events:none; }
  .action-menu-item--danger   { color:#e03535; } .action-menu-item--danger:hover { background:#fff0f0 !important; }
  .action-menu-item:disabled  { opacity:.4; cursor:not-allowed; }
  .action-menu-icon        { font-size:12px; width:16px; flex-shrink:0; }
  .action-menu-icon--view  { color:#0d9488; }
  .action-menu-icon--edit  { color:#004AAD; }
  .action-menu-icon--blue  { color:#004AAD; }
  .action-menu-icon--green { color:#16a34a; }
  .action-menu-icon--orange { color:#e08c00; }
  
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { display:flex; align-items:center; gap:8px; border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; background:transparent; border:none; width:100%; text-align:left; cursor:pointer; }
  .dropdown-item:hover:not(:disabled) { background:#f4f7fb; }
  .dropdown-item:disabled { opacity:.4; cursor:not-allowed; }
  .dropdown-divider { border-color:#f0f4f8; margin:4px 0; }
  
  /* ── Modals ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--blue    { background:rgba(0,74,173,.1);  color:#004AAD; }
  .modal-icon--warning { background:rgba(224,140,0,.1); color:#e08c00; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
  .modal-btn--blue   { background:#004AAD; color:#fff; } .modal-btn--blue:hover { background:#003b8e; }
  .modal-btn:disabled { opacity:.6; cursor:not-allowed; }
  .p-0 { padding:0; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } .billing-meta-strip { flex-direction:column; } .meta-item { border-right:none; border-bottom:1px solid #e4eaf2; } }
  </style>